import React, { useRef, useEffect, useState } from 'react';
import Image from 'next/image';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, {
  Autoplay,
  Navigation,
  A11y,
  Lazy,
  EffectFade,
  Controller
} from 'swiper';
import 'swiper/css';
import 'swiper/css/lazy';
import 'swiper/css/a11y';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/thumbs';
import 'swiper/css/effect-fade';

SwiperCore.use([Autoplay, Navigation, Lazy, A11y, EffectFade, Controller]);

import {
  LeftSwipperArrows,
  RightSwipperArrows
} from '../../shared-components/SwipperArrows/SwipperArrows';

import { useMediaQuery } from '../../hooks/mediaQuery';

interface IProps {
  attraction: any;
  setShowModal: (show: boolean) => void;
}

const AttractionModal = ({ attraction, setShowModal }: IProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [hasTicket, setHasTicket] = useState(false);
  const isMobile = useMediaQuery(767);
  const isTablet = useMediaQuery(1439) && !isMobile;
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLastSlide, setIsLastSlide] = useState(false);
  const [isFirstSlide, setIsFirstSlide] = useState(false);

  const handleSlideChange = (swiper: any) => {
    setCurrentImageIndex(swiper.activeIndex);
    setIsLastSlide(swiper.isEnd);
    setIsFirstSlide(swiper.isBeginning);
  };

  const handleSwiperInit = (swiper: any) => {
    setCurrentImageIndex(swiper.activeIndex);
    setIsFirstSlide(swiper.isBeginning);
    setIsLastSlide(swiper.isEnd);
  };

  let descriptionLimit = 270;
  if (isMobile) {
    descriptionLimit = 81;
  } else if (isTablet) {
    descriptionLimit = 170;
  }

  useEffect(() => {
    if (attraction?.tickets) {
      setHasTicket(true);
    } else {
      setHasTicket(false);
    }
  }, [attraction]);

  const hours = attraction?.hoursOfOperation?.split('\n') || [];
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });

  let todayHours = hours.find((line: string) =>
    line.toLowerCase().includes(today.toLowerCase())
  );

  if (!todayHours) {
    todayHours = hours.find((line: string) =>
      line.toLowerCase().includes('daily')
    );
  }

  const displayHours = todayHours || 'Closed today';

  const handleBuyTicket = () => {
    window.open(`/${attraction.slug}?showPriceModal=true`, '_blank');
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-[10999] flex justify-center items-center overflow-hidden h-full w-full"
      data-nextjs-dialog-overlay="true"
      onClick={e => {
        if (e.target === e.currentTarget) setShowModal(false);
      }}
    >
      <div
        ref={modalRef}
        className="bg-white relative rounded-[2rem] w-[90%] h-[75%] md:w-[65%] lg:w-[55%] xl:w-[45%] 2xl:w-[32%] md:h-[70%] xl:h-[75%]"
        data-nextjs-dialog="true"
      >
        <div className="relative w-full h-[50%] md:h-[53%] xl:h-[55%] rounded-t-[2rem] overflow-hidden">
          {attraction?.heroMedia?.length > 0 ? (
            <Swiper
              slidesPerView={1}
              onSlideChange={handleSlideChange}
              onInit={handleSwiperInit}
              navigation={{ prevEl: '.prevSlide', nextEl: '.nextSlide' }}
              className="w-full h-full"
            >
              {attraction.heroMedia.map((media: any, index: number) => (
                <SwiperSlide key={index}>
                  <div className="relative w-full h-full">
                    <Image
                      src={media.url}
                      alt={media.alt || `${attraction.name} in 2024`}
                      fill
                      className="rounded-t-[2rem] object-cover"
                      loading="lazy"
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          ) : (
            <div className="flex items-center justify-center h-[250px] text-gray-500">
              No images available
            </div>
          )}

          <span
            className="cursor-pointer absolute top-4 right-5 z-[100]"
            onClick={() => setShowModal(false)}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x="0.398438"
                y="0.5"
                width="20.5989"
                height="20"
                rx="10"
                fill="#DEDEDE"
              />
              <path
                d="M14.7681 7.51358C14.8398 7.44442 14.897 7.36169 14.9363 7.27021C14.9757 7.17872 14.9964 7.08032 14.9973 6.98074C14.9982 6.88115 14.9793 6.78238 14.9416 6.69019C14.904 6.598 14.8483 6.51424 14.7779 6.44379C14.7076 6.37333 14.6238 6.31761 14.5317 6.27985C14.4395 6.2421 14.3408 6.22307 14.2412 6.22389C14.1416 6.22471 14.0432 6.24535 13.9517 6.28462C13.8602 6.32388 13.7774 6.38098 13.7081 6.45258L10.7021 9.45758L7.69715 6.45258C7.62848 6.37889 7.54568 6.31979 7.45368 6.27879C7.36168 6.2378 7.26237 6.21576 7.16167 6.21398C7.06097 6.21221 6.96094 6.23073 6.86755 6.26845C6.77416 6.30617 6.68933 6.36232 6.61811 6.43354C6.54689 6.50476 6.49074 6.58959 6.45302 6.68298C6.4153 6.77637 6.39678 6.8764 6.39855 6.9771C6.40033 7.0778 6.42237 7.17711 6.46336 7.26911C6.50436 7.36111 6.56346 7.44391 6.63715 7.51258L9.64015 10.5186L6.63515 13.5236C6.50267 13.6658 6.43054 13.8538 6.43397 14.0481C6.4374 14.2424 6.51611 14.4278 6.65352 14.5652C6.79094 14.7026 6.97632 14.7813 7.17062 14.7847C7.36492 14.7882 7.55297 14.7161 7.69515 14.5836L10.7021 11.5786L13.7071 14.5846C13.8493 14.7171 14.0374 14.7892 14.2317 14.7858C14.426 14.7823 14.6114 14.7036 14.7488 14.5662C14.8862 14.4288 14.9649 14.2434 14.9683 14.0491C14.9717 13.8548 14.8996 13.6667 14.7671 13.5246L11.7641 10.5186L14.7681 7.51358Z"
                fill="black"
              />
            </svg>
          </span>

          {/* Arrows */}
          <div
            id="slider-btn"
            className="absolute top-1/2 left-0 right-0 flex justify-between px-4 z-[50]"
          >
            <div className="prevSlide cursor-pointer">
              <LeftSwipperArrows isFirstSlide={isFirstSlide} />
            </div>
            <div className="nextSlide cursor-pointer">
              <RightSwipperArrows isLastSlide={isLastSlide} />
            </div>
          </div>

          {/* Dot Indicators */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-[200]">
            <div className="flex gap-2">
              {attraction.heroMedia.map((_: any, index: number) => (
                <div
                  key={index}
                  className={`w-2.5 h-2.5 rounded-full ${
                    currentImageIndex === index ? 'bg-[#ff4545]' : 'bg-gray-400'
                  }`}
                ></div>
              ))}
            </div>
          </div>
        </div>

        {/* Modal content */}
        <div className="p-4 md:px-6  h-[50%] md:h-[47%] xl:h-[45%] flex flex-col justify-between">
          <div className="w-full flex flex-row justify-between mt-4">
            <span className="text-[16px] md:text-[20px] xl:text-[24px] font-semibold">
              {attraction.name}
            </span>
            {hasTicket && (
              <div
                className="bg-primary text-[14px] md:text-[16px] xl:text-[18px] text-white rounded-lg p-1 cursor-pointer text-center w-[110px] h-[30px] md:w-[100px] xl:w-[120px] xl:h-[35px]"
                onClick={handleBuyTicket}
              >
                Book Now
              </div>
            )}
          </div>

          <div className="flex flex-col justify-between flex-grow">
            <div className="text-black text-[14px] mt-1 md:mt-2 md:text-[15px] xl:text-[16px]">
              {attraction.shortDescription ? (
                <>
                  {attraction.shortDescription.length > descriptionLimit ? (
                    <>
                      {attraction.shortDescription.slice(0, descriptionLimit)}
                      ...
                      <button
                        className="font-bold text-black ml-1"
                        onClick={() =>
                          window.open(`/${attraction.slug}`, '_blank')
                        }
                      >
                        show more
                      </button>
                    </>
                  ) : (
                    attraction.shortDescription
                  )}
                </>
              ) : (
                <p>
                  Explore this amazing attraction and discover what makes it a
                  must-visit. Stay tuned for more details!
                </p>
              )}
            </div>

            {attraction.address && (
              <p className="text-[14px] md:text-[15px] xl:text-[16px]">
                <strong>Location:</strong> {attraction.address}
              </p>
            )}
            {attraction?.googleData && (
              <p className="text-[14px] md:text-[15px] xl:text-[16px]">
                <strong>Opening Hours:</strong>{' '}
                {attraction?.googleData?.current_opening_hours?.weekday_text?.[
                  new Date().getDay() === 0 ? 6 : new Date().getDay() - 1
                ] || 'Not Mentioned for Today'}
              </p>
            )}

            <button
              onClick={() => window.open(`/${attraction.slug}`, '_blank')}
              className="w-full text-[14px] md:text-[16px] px-6 py-2 rounded-lg bg-primary text-white"
            >
              View Full Details
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttractionModal;
